import React, { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Tag from "./Tag";

import { OBJECT_TYPE, TAGS } from "../../../../util/Data/DataModeling";
import { getUUID } from "../../../../util/common";

const useStyles = makeStyles()({
  Panel: {
    padding: "20px 25px",
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    boxShadow: "none",
    maxHeight: "450px",
    minHeight: "250px",
    overflowY: "auto",
  },
});

const Tags = forwardRef(function Tags({ objectType }, ref) {
  const { classes } = useStyles();

  const [tags, setTags] = useState([]);

  const resetData = useCallback(() => {
    setTags([]);
  }, []);

  const handleTagValueChange = useCallback((index, key, value) => {
    setTags((prev) => {
      prev[index][key] = value;
      return [...prev];
    });
  }, []);

  const handleTagDelete = useCallback((index) => {
    setTags((prev) => {
      const tgs = [...prev];
      tgs.splice(index, 1);
      return tgs;
    });
  }, []);

  const handleAddTag = useCallback(() => {
    setTags((prev) => [...prev, { id: getUUID(), TagKey: "", TagValue: "" }]);
  }, []);

  const anyErrorInTags = useCallback(() => {
    const keyLimitationError = tags.some(({ TagKey }) => TagKey.length === 0 || TagKey.length > 50);
    let repetitiveKeyError = false;
    if (!keyLimitationError) {
      const keys = tags.map(({ TagKey }) => TagKey);
      repetitiveKeyError = keys.length !== new Set(keys).size;
    }
    return { keyLimitationError, repetitiveKeyError };
  }, [tags]);

  const getTags = useCallback(() => {
    return tags;
  }, [tags]);

  const setTagsFromParent = useCallback((tagsFromParent) => {
    setTags(tagsFromParent);
  }, []);

  useImperativeHandle(ref, () => ({ handleAddTag, getTags, anyErrorInTags, setTagsFromParent, resetData }));

  return (
    <Box
      className={classes.Panel}
      sx={{
        ...(!tags.length ? { display: "flex", justifyContent: "center", alignItems: "center" } : {}),
        width: objectType === OBJECT_TYPE.DataSource ? { md: "100%", lg: "50%" } : "100%",
      }}
    >
      {tags.length > 0 ? (
        <Grid container spacing={2}>
          {tags.map((tag, index) => (
            <Tag
              key={tag.id}
              index={index}
              tag={tag}
              handleTagValueChange={handleTagValueChange}
              handleTagDelete={handleTagDelete}
              tags={tags}
            />
          ))}
        </Grid>
      ) : (
        <>{`No ${TAGS.plural} Defined`}</>
      )}
    </Box>
  );
});

Tags.propTypes = {
  objectType: PropTypes.oneOf([OBJECT_TYPE.DataSource, OBJECT_TYPE.Entity]).isRequired,
};

export default Tags;
